import React from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-th-bg text-th-text">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-th-muted hover:text-th-text mb-8 transition-colors"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <h1 className="text-2xl font-bold mb-2">Terms of Service</h1>
        <p className="text-sm text-th-muted mb-8">Last updated: April 1, 2026</p>

        <div className="space-y-6 text-sm text-th-text-sec leading-relaxed">
          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Pinned ("the App"), operated by Andres Jaramillo ("we", "our", "us"),
              you agree to be bound by these Terms of Service and our{" "}
              <Link to="/privacy" className="text-th-accent hover:underline">Privacy Policy</Link>.
              If you do not agree, do not use the App.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">2. Eligibility</h2>
            <p>
              You must be at least 18 years old to use Pinned. By creating an account, you represent and
              warrant that you are at least 18 years of age.
            </p>
            <p className="mt-2">
              We do not knowingly collect personal data from anyone under 18 years of age. If we become
              aware that we have collected data from a person under 18, we will promptly delete the account
              and all associated data.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">3. Your Account</h2>
            <p>
              You are responsible for maintaining the security of your account credentials. You agree to
              provide accurate information during registration and to keep it up to date. You may not share
              your credentials or allow others to access your account. We reserve the right to suspend or
              terminate accounts that violate these terms.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">4. User Content</h2>
            <p>
              You retain ownership of the photos, reviews, and other content you post on Pinned ("User Content").
              By posting content, you grant us a worldwide, non-exclusive, royalty-free, sublicensable license
              to use, display, reproduce, and distribute your User Content within and in connection with the App,
              including for promotional purposes such as social media, app store listings, and marketing materials.
            </p>
            <p className="mt-2">
              This license survives deletion of your account for content that has already been distributed,
              cached, or used in promotional materials prior to deletion. We will make reasonable efforts to
              cease use of your content in new materials after account deletion.
            </p>
            <p className="mt-2">
              You represent that you have the right to share any content you upload and that it does not
              infringe on the rights of any third party.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">5. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Post content that is illegal, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable.</li>
              <li>Upload content that you do not have the right to share, including copyrighted material owned by others.</li>
              <li>Impersonate any person or entity, or falsely represent your affiliation with any person or entity.</li>
              <li>Use the App for any commercial purpose without our prior written consent, including advertising or solicitation.</li>
              <li>Attempt to interfere with the proper functioning of the App, including hacking, scraping, or introducing malware.</li>
              <li>Post deliberately misleading reviews or location data.</li>
              <li>Create multiple accounts for deceptive purposes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">6. Copyright and DMCA</h2>
            <p>
              We respect intellectual property rights. If you believe content on Pinned infringes your copyright,
              you may submit a takedown notice to our designated agent at{" "}
              <a href="mailto:hello@usepinned.com" className="text-th-accent hover:underline">hello@usepinned.com</a>{" "}
              with the following information:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>A description of the copyrighted work you claim has been infringed.</li>
              <li>A description of the infringing material and its location within the App.</li>
              <li>Your contact information (name, address, email, phone number).</li>
              <li>A statement that you have a good faith belief that the use is not authorized by the copyright owner.</li>
              <li>A statement, under penalty of perjury, that the information in the notice is accurate and that you are the copyright owner or authorized to act on their behalf.</li>
              <li>Your physical or electronic signature.</li>
            </ul>
            <p className="mt-2">
              Upon receipt of a valid notice, we will remove or disable access to the allegedly infringing content
              and notify the uploader. Repeat infringers may have their accounts terminated.
            </p>
            <p className="mt-2">
              <strong>Counter-Notice:</strong> If you believe your content was removed in error, you may submit
              a counter-notification to the same email address with: (a) your contact information, (b) identification
              of the removed content, (c) a statement under penalty of perjury that you have a good faith belief
              the content was removed by mistake, and (d) consent to the jurisdiction of the federal courts in
              Florida. We will forward counter-notices to the original complainant and may restore the content
              after 10 business days if no legal action is filed.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">7. Location Data</h2>
            <p>
              Pinned extracts GPS coordinates from photo metadata (EXIF data) to place pins on the map.
              By uploading photos containing location data, you explicitly consent to that location being
              displayed publicly. You can review the detected location before publishing and choose not to
              upload photos that contain location data you wish to keep private.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">8. Push Notifications</h2>
            <p>
              Pinned may send push notifications about activity on your posts (e.g., new comments, view milestones).
              You can enable or disable notifications at any time through your device settings. Notification
              preferences are opt-in and not required to use the App.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">9. Intellectual Property</h2>
            <p>
              The App, its design, features, and branding (including the Pinned name, logo, and interface)
              are owned by us and protected by intellectual property laws. You may not copy, modify, or
              distribute any part of the App without our written permission.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">10. Content Moderation, Reporting and Blocking</h2>
            <p>
              We reserve the right to remove any content that violates these terms or that we deem
              inappropriate at our sole discretion, without prior notice. We are not obligated to
              monitor all content but may do so at our discretion. We have no tolerance for
              objectionable content or abusive behavior. Content that is objectionable, and users who
              post it or who abuse others, may be removed from Pinned at any time without notice.
            </p>
            <p className="mt-2">
              Pinned provides in-app tools to keep the map trustworthy and safe. You can report any
              pin or comment that you believe violates these terms, directly from that content. You
              can also block any user; blocking hides their content from you and prevents them from
              interacting with your content.
            </p>
            <p className="mt-2">
              We review all reports and act on content that violates these terms within 24 hours of a
              report, including removing the content and, where warranted, suspending or terminating
              the account responsible. To report something you cannot reach in the App, email us at
              hello@usepinned.com.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">11. Third-Party Services</h2>
            <p>
              Pinned relies on third-party infrastructure providers for image hosting, database storage,
              email delivery, and app hosting. Your use of the App involves processing of your data by
              these providers as described in our{" "}
              <Link to="/privacy" className="text-th-accent hover:underline">Privacy Policy</Link>.
              We are not responsible for the practices of third-party services beyond what is described
              in our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">12. Disclaimers</h2>
            <p>
              The App is provided "as is" and "as available" without warranties of any kind, either express
              or implied, including but not limited to implied warranties of merchantability, fitness for a
              particular purpose, and non-infringement. We do not guarantee the accuracy, completeness,
              or reliability of any content posted by users, including reviews, ratings, and location information.
            </p>
            <p className="mt-2">
              Travel involves inherent risks. We are not responsible for any decisions you make based on
              information found on Pinned, including but not limited to visiting locations, purchasing
              food or services, or any resulting injury, loss, or damage.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">13. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, our total liability to you for any claims arising
              from or related to your use of the App shall not exceed the greater of (a) one hundred
              US dollars ($100.00) or (b) the total amount you have paid us in the twelve (12) months
              preceding the claim.
            </p>
            <p className="mt-2">
              In no event shall we be liable for any indirect, incidental, special, consequential, or
              punitive damages, or any loss of profits, revenue, data, or goodwill, whether incurred
              directly or indirectly, arising from your use of the App.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">14. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Pinned and its operator from any claims,
              damages, losses, liabilities, and expenses (including reasonable attorney's fees) arising
              from your use of the App, your User Content, or your violation of these terms.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">15. Dispute Resolution and Arbitration</h2>
            <p>
              Any dispute, claim, or controversy arising out of or relating to these terms or your use of
              the App shall be resolved by binding arbitration administered in accordance with the rules of
              the American Arbitration Association (AAA). Arbitration shall take place in the State of Florida,
              USA, or remotely at your election.
            </p>
            <p className="mt-2">
              <strong>Class Action Waiver:</strong> You agree that any dispute resolution proceedings will be
              conducted only on an individual basis and not in a class, consolidated, or representative action.
            </p>
            <p className="mt-2">
              <strong>Small Claims Exception:</strong> Either party may bring an individual action in small
              claims court for disputes within that court's jurisdiction.
            </p>
            <p className="mt-2">
              <strong>Opt-Out:</strong> You may opt out of this arbitration agreement by sending written notice
              to <a href="mailto:hello@usepinned.com" className="text-th-accent hover:underline">hello@usepinned.com</a>{" "}
              within 30 days of creating your account. If you opt out, disputes will be resolved in the courts
              of the State of Florida.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">16. Account Termination and Data Deletion</h2>
            <p>
              You may delete your account at any time through the App settings. Upon account deletion,
              we will permanently delete your profile data, posts, comments, and associated records within
              30 days. Some data may be retained in encrypted backups for up to 90 days before being
              permanently purged.
            </p>
            <p className="mt-2">
              We reserve the right to suspend or terminate your account at our sole discretion if you
              violate these terms, with or without prior notice.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">17. Changes to Terms</h2>
            <p>
              We may update these terms from time to time. Continued use of the App after changes
              constitutes acceptance of the updated terms. Material changes will be communicated through
              the App or via email at least 14 days before taking effect.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">18. Google Play and Apple App Store</h2>
            <p>
              If you downloaded the App from Google Play, you acknowledge that these terms are between
              you and Pinned only, not with Google. Google has no obligation to provide maintenance, support,
              or warranty for the App. Google is a third-party beneficiary of these terms and may enforce
              them against you. If you downloaded the App from the Apple App Store, you acknowledge that
              these terms are between you and Pinned only, not with Apple. Apple has no obligation to
              provide maintenance or support for the App and is not responsible for any claims relating
              to it. Apple and its subsidiaries are third-party beneficiaries of these terms and may
              enforce them against you.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">19. Severability</h2>
            <p>
              If any provision of these terms is found to be unenforceable or invalid, that provision shall
              be limited or eliminated to the minimum extent necessary, and the remaining provisions shall
              continue in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">20. Governing Law</h2>
            <p>
              These terms are governed by the laws of the State of Florida, United States, without regard
              to its conflict of law provisions. For matters not subject to arbitration, any disputes shall
              be resolved in the courts of the State of Florida.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">21. Contact</h2>
            <p>
              Questions about these terms? Email us at{" "}
              <a href="mailto:hello@usepinned.com" className="text-th-accent hover:underline">hello@usepinned.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
